import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { createControleDossierLot, updateControleDossierLot } from "../../../services/api";
import { defaultFormData, mergeFormData } from "./bisDossierData";
import { VARIANT } from "./bisVariant";
import { CONTROLE_BIS_DOSSIER_TYPE } from "../shared/controleConstants";
import { apiErrorMessage } from "../shared/controleFormat";
import { useChronicBilling } from "../shared/hooks/useChronicBilling";
import { useControleDossiers, useSaveShortcut } from "../shared/hooks/useControleDossiers";
import { useControlePatients } from "../shared/hooks/useControlePatients";
import { usePatientControlActions, usePatientControlSummary, usePrintReport } from "../shared/hooks/usePatientControlForm";
import { usePatientEditing } from "../shared/hooks/usePatientEditing";
import { isPatientControlFullyValidated } from "../shared/patientControlRules";

const WORKFLOW_TABS = ["controlePatient", "dossierLot", "postProduction"];

function bisDossiers(dossiers, ehpadId) {
    const controleDossiers = dossiers.filter(dossier => dossier.donnees?.meta?.dossierType === CONTROLE_BIS_DOSSIER_TYPE);
    return ehpadId ? controleDossiers.filter(dossier => String(dossier.ehpadId) === String(ehpadId)) : controleDossiers;
}

// Le planning robot ouvre l'écran sur ?dossierId=…&tab=… : le dossier s'ouvre dès que la liste est chargée.
function useOpenDossierFromUrl(workspace, { setActiveControleTab, setActiveProductionBlockKey }) {
    const location = useLocation();
    const { loading, activeDossier, dossiers, openDossier } = workspace;
    useEffect(() => {
        if (loading || activeDossier) return;
        const params = new URLSearchParams(location.search || "");
        const dossierId = params.get("dossierId");
        const requestedTab = params.get("tab");
        if (!dossierId) return;
        const dossier = dossiers.find(item => String(item.id) === String(dossierId));
        if (!dossier) return;
        const nextTab = WORKFLOW_TABS.includes(requestedTab) ? requestedTab : "controlePatient";
        openDossier(dossier);
        setActiveControleTab(nextTab);
        if (nextTab === "dossierLot") setActiveProductionBlockKey("lancement");
    }, [activeDossier, dossiers, loading, location.search, openDossier, setActiveControleTab, setActiveProductionBlockKey]);
}

// Un dossier Bis est créé typé, puis initialisé avec l'EHPAD et la date de production.
function useBisDossierCreation({ workspace, setError, setActiveControleTab }) {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const createDossier = async (ehpadId, dateProduction) => {
        if (!ehpadId || !dateProduction) {
            setError("Sélectionnez un EHPAD et une date de production.");
            return;
        }
        setError("");
        workspace.setSaving(true);
        try {
            const createdDossier = (await createControleDossierLot(Number(ehpadId), dateProduction, CONTROLE_BIS_DOSSIER_TYPE)).data;
            const createdEhpad = workspace.ehpads.find(ehpad => String(ehpad.id) === String(ehpadId));
            const initialData = mergeFormData({
                ...(createdDossier.donnees || {}),
                meta: {
                    ...(createdDossier.donnees?.meta || {}),
                    dossierType: CONTROLE_BIS_DOSSIER_TYPE,
                    ehpad: createdDossier.ehpadNom || createdEhpad?.nom || "",
                    dateProduction,
                },
            });
            const dossier = (await updateControleDossierLot(createdDossier.id, {
                status: createdDossier.status || "BROUILLON",
                donnees: initialData,
            })).data;
            workspace.openDossier(dossier);
            setActiveControleTab("controlePatient");
            await workspace.loadDossiers();
            setShowCreateModal(false);
        } catch (error) {
            setError(apiErrorMessage(error, "Création du dossier impossible."));
        } finally {
            workspace.setSaving(false);
        }
    };
    return {
        showCreateModal,
        openCreateModal: () => {
            setShowCreateModal(true);
            setError("");
        },
        closeCreateModal: () => setShowCreateModal(false),
        createDossier,
    };
}

// Les pièces jointes importées pour une section (point 6 de la pré-production) sont étiquetées avec cette section.
function useSectionAttachments(workspace) {
    return (files, section = "") => workspace.uploadAttachments(files, section ? async (latestDossier, selectedFiles) => {
        const uploadedNames = new Set(selectedFiles.map(file => file.name));
        const donnees = mergeFormData(latestDossier.donnees);
        const piecesJointes = (donnees.piecesJointes || []).map(attachment => (
            uploadedNames.has(attachment.fileName) && !attachment.section ? { ...attachment, section } : attachment
        ));
        return (await updateControleDossierLot(latestDossier.id, {
            status: latestDossier.status || workspace.activeDossier.status || "BROUILLON",
            donnees: { ...donnees, piecesJointes },
        })).data;
    } : null);
}

// Validation de la pré-production : elle fige la partie pré-production et les patients entièrement validés.
function usePreProduction({ workspace, user, setError, withPlanningProgress, uploadAttachments }) {
    const [showValidatePreProductionConfirm, setShowValidatePreProductionConfirm] = useState(false);
    const preProductionLocked = Boolean(workspace.formData.meta?.preProductionValidatedAt);
    const isPatientFrozenAfterPreProduction = (patientId, sourceData = workspace.formData) => {
        if (!preProductionLocked || !patientId) return false;
        return isPatientControlFullyValidated((sourceData.controlePatients || {})[patientId] || {});
    };

    const validatePreProduction = async () => {
        if (!workspace.activeDossier || preProductionLocked || workspace.saving) return;
        setShowValidatePreProductionConfirm(false);
        const nextData = withPlanningProgress({
            ...workspace.formData,
            meta: {
                ...(workspace.formData.meta || {}),
                preProductionValidatedAt: new Date().toISOString(),
                preProductionValidatedBy: user?.username || user?.email || "",
            },
        });
        workspace.setSaving(true);
        setError("");
        try {
            const response = await updateControleDossierLot(workspace.activeDossier.id, {
                status: workspace.activeDossier.status || "BROUILLON",
                donnees: nextData,
            });
            workspace.setActiveDossier(response.data);
            workspace.setFormData(mergeFormData(response.data.donnees));
            await workspace.loadDossiers();
        } catch (error) {
            setError(apiErrorMessage(error, "Validation de la pré-production impossible."));
        } finally {
            workspace.setSaving(false);
        }
    };

    return {
        preProductionLocked,
        isPatientFrozenAfterPreProduction,
        showValidatePreProductionConfirm,
        setShowValidatePreProductionConfirm,
        requestValidatePreProduction: () => {
            if (preProductionLocked || workspace.saving) return;
            setShowValidatePreProductionConfirm(true);
        },
        validatePreProduction,
        uploadPreProductionAttachment: files => {
            if (!preProductionLocked) uploadAttachments(files);
        },
        uploadPreProductionPoint6Attachment: files => {
            if (!preProductionLocked) uploadAttachments(files, "preprodPoint6");
        },
    };
}

function useProductionSections(workspace) {
    const [activeProductionBlockKey, setActiveProductionBlockKey] = useState("lancement");
    const [activePostProductionBlockKey, setActivePostProductionBlockKey] = useState("pouchInspector");
    return {
        activeProductionBlockKey,
        setActiveProductionBlockKey,
        activePostProductionBlockKey,
        setActivePostProductionBlockKey,
        updateSection: (section, patch) => workspace.setFormData(data => ({ ...data, [section]: { ...(data[section] || {}), ...patch } })),
        updateRows: (section, rows) => workspace.setFormData(data => ({ ...data, [section]: rows })),
    };
}

function usePatientPanels() {
    const [showControlePatientModal, setShowControlePatientModal] = useState(false);
    const [showLastDossierInline, setShowLastDossierInline] = useState(false);
    const [showInlinePatientControl, setShowInlinePatientControl] = useState(true);
    return {
        showControlePatientModal,
        setShowControlePatientModal,
        showLastDossierInline,
        setShowLastDossierInline,
        showInlinePatientControl,
        setShowInlinePatientControl,
    };
}

// État et actions de l'écran Contrôle Bis, exposés aux vues sous forme d'un seul objet.
export function useControleBis() {
    const { user } = useAuth();
    const [activeControleTab, setActiveControleTab] = useState("controlePatient");
    const [error, setError] = useState("");
    const workspace = useControleDossiers({ onError: setError, mergeFormData, defaultFormData });
    const sections = useProductionSections(workspace);
    const uploadAttachments = useSectionAttachments(workspace);
    // Le planning robot affiche l'avancement des patients : il est enregistré avec chaque sauvegarde du dossier.
    // Les compteurs sont connus plus bas dans le rendu ; la fonction ne les lit qu'au moment d'enregistrer.
    let planningCounts = { total: 0, validated: 0 };
    const withPlanningProgress = data => ({
        ...data,
        meta: { ...(data.meta || {}), robotPlanningPatientTotal: planningCounts.total, robotPlanningPatientValidated: planningCounts.validated },
    });
    const preProduction = usePreProduction({ workspace, user, setError, withPlanningProgress, uploadAttachments });
    const isFrozen = preProduction.isPatientFrozenAfterPreProduction;
    const activeControleMode = workspace.selectedEhpad?.controleMode || null;
    const activeDossierEhpadId = workspace.activeDossier?.ehpadId || workspace.selectedEhpadId;
    const controle = useControlePatients({
        ehpadId: activeDossierEhpadId,
        productionSubcategory: workspace.formData.meta?.productionSubcategory || "",
        searchLocked: preProduction.preProductionLocked,
        onError: setError,
    });
    const editing = usePatientEditing(controle, { isFrozen, onError: setError });
    const chronic = useChronicBilling(controle, { user, isFrozen, onError: setError });
    const actions = usePatientControlActions(workspace, { user, isFrozen });
    const summary = usePatientControlSummary({
        patients: controle.patients,
        controls: workspace.formData.controlePatients,
        variant: VARIANT,
        controleMode: activeControleMode,
        useSharedComment: true,
    });
    planningCounts = { total: summary.activeControlePatientCount, validated: summary.validatedPatientCount };
    const report = usePrintReport({ variant: VARIANT, workspace, issues: summary.blockingPatientIssues, remainingPatientCount: summary.remainingPatientCount });
    const creation = useBisDossierCreation({ workspace, setError, setActiveControleTab });
    useOpenDossierFromUrl(workspace, { setActiveControleTab, setActiveProductionBlockKey: sections.setActiveProductionBlockKey });
    const filteredDossiers = useMemo(() => bisDossiers(workspace.dossiers, workspace.selectedEhpadId), [workspace.dossiers, workspace.selectedEhpadId]);

    const saveDossier = (status = workspace.activeDossier?.status || "BROUILLON") => workspace.saveDossier(status, withPlanningProgress(workspace.formData));
    useSaveShortcut(Boolean(workspace.activeDossier) && !workspace.saving, () => saveDossier("BROUILLON"));

    return {
        ...actions,
        ...summary,
        ...sections,
        ...preProduction,
        ...usePatientPanels(),
        activeControleMode,
        activeControleTab,
        activeDossierEhpadId,
        chronic,
        controle,
        creation,
        editing,
        error,
        filteredDossiers,
        openDossier: dossier => {
            workspace.openDossier(dossier);
            setActiveControleTab("controlePatient");
        },
        printControlePatientReport: report.print,
        report,
        saveDossier,
        setActiveControleTab,
        uploadAttachments,
        user,
        workspace,
    };
}
